/**
 * Pure helpers for market data selection (shared pattern with onboarding).
 * Cabinet uses {@link cabinetMarketKey} — provider `name` as the selection key.
 */

export function findProviderByMarketId(marketId, providers, getMarketId) {
  return providers.find((p) => getMarketId(p) === marketId);
}

export function filterMarketdataprovidersForTradingPlatform(
  providers,
  tradingPlatformRef
) {
  if (!Array.isArray(providers)) return [];
  if (!tradingPlatformRef) return providers;
  return providers.filter((p) =>
    Array.isArray(p.tradingPlatformRefs)
      ? p.tradingPlatformRefs.includes(tradingPlatformRef)
      : false
  );
}

export function findProviderByGroupAndName(group, market, providers) {
  return providers.find((p) => p.group === group && p.name === market);
}

export function buildMutualExclusivityGraph(providers) {
  const graph = new Map();
  if (!Array.isArray(providers)) {
    return graph;
  }
  const addEdge = (a, b) => {
    if (!a || !b || a === b) return;
    if (!graph.has(a)) graph.set(a, new Set());
    if (!graph.has(b)) graph.set(b, new Set());
    graph.get(a).add(b);
    graph.get(b).add(a);
  };
  providers.forEach((p) => {
    if (!p?.reference) return;
    if (!Array.isArray(p.mutuallyExclusive) || p.mutuallyExclusive.length === 0) {
      return;
    }
    p.mutuallyExclusive.forEach((otherRef) => {
      addEdge(p.reference, otherRef);
    });
  });
  return graph;
}

export function refsMutuallyExclusive(refA, refB, graph) {
  if (!refA || !refB || refA === refB) return false;
  return graph.get(refA)?.has(refB) ?? false;
}

export function collectIncludedMarketIdsFromSelections(
  selectedMarketIds,
  providers,
  getMarketId
) {
  const out = new Set();
  if (!Array.isArray(selectedMarketIds) || !Array.isArray(providers)) {
    return out;
  }
  const findByRef = (ref) => providers.find((p) => p.reference === ref);

  const visitFeed = (feed, visitedRefs) => {
    if (!feed?.consistsOf || !Array.isArray(feed.consistsOf)) return;
    feed.consistsOf.forEach((ref) => {
      if (visitedRefs.has(ref)) return;
      visitedRefs.add(ref);
      const child = findByRef(ref);
      if (child) {
        out.add(getMarketId(child));
        visitFeed(child, visitedRefs);
      }
    });
  };

  selectedMarketIds.forEach((mId) => {
    const feed = findProviderByMarketId(mId, providers, getMarketId);
    if (feed) visitFeed(feed, new Set());
  });
  return out;
}

export function addIncludedFeedsToMarkets(markets, feed, findByRef, getMarketId) {
  if (!feed?.consistsOf || !Array.isArray(feed.consistsOf)) return;
  feed.consistsOf.forEach((ref) => {
    const includedFeed = findByRef(ref);
    if (includedFeed) {
      const includedMarketId = getMarketId(includedFeed);
      if (!markets.includes(includedMarketId)) {
        markets.push(includedMarketId);
        addIncludedFeedsToMarkets(markets, includedFeed, findByRef, getMarketId);
      }
    }
  });
}

export function removeIncludedFeedsFromMarkets(markets, feed, findByRef, getMarketId) {
  if (!feed?.consistsOf || !Array.isArray(feed.consistsOf)) return;
  feed.consistsOf.forEach((ref) => {
    const includedFeed = findByRef(ref);
    if (includedFeed) {
      const includedMarketId = getMarketId(includedFeed);
      const idx = markets.indexOf(includedMarketId);
      if (idx >= 0) markets.splice(idx, 1);
      removeIncludedFeedsFromMarkets(markets, includedFeed, findByRef, getMarketId);
    }
  });
}

export function removeMarketSelection(
  markets,
  marketId,
  providers,
  getMarketId,
  findByRef
) {
  const feed = findProviderByMarketId(marketId, providers, getMarketId);
  const idx = markets.indexOf(marketId);
  if (idx < 0) return;
  markets.splice(idx, 1);
  if (feed?.reference) {
    providers.forEach((m) => {
      if (m.dependencyRefs && m.dependencyRefs.includes(feed.reference)) {
        const mid2 = getMarketId(m);
        const i2 = markets.indexOf(mid2);
        if (i2 >= 0) markets.splice(i2, 1);
      }
    });
  }
  if (feed) {
    removeIncludedFeedsFromMarkets(markets, feed, findByRef, getMarketId);
  }
}

export function removeMutuallyExclusiveSelections(
  markets,
  incomingRef,
  providers,
  getMarketId,
  findByRef,
  graph
) {
  if (!incomingRef || !graph.size) return;
  const toRemove = [];
  markets.forEach((mid) => {
    const p = findProviderByMarketId(mid, providers, getMarketId);
    if (p?.reference && refsMutuallyExclusive(incomingRef, p.reference, graph)) {
      toRemove.push(mid);
    }
  });
  toRemove.forEach((mid) => {
    removeMarketSelection(markets, mid, providers, getMarketId, findByRef);
  });
}

export function normalizeConsistsOfPackageSelections(
  markets,
  providers,
  getMarketId,
  findByRef
) {
  if (!Array.isArray(markets) || !Array.isArray(providers)) return;

  const parents = providers.filter(
    (p) =>
      Array.isArray(p.consistsOf) &&
      p.consistsOf.length >= 2 &&
      p.consistsOf.every((ref) => !!findByRef(ref))
  );

  let changed = true;
  let guard = 0;
  while (changed && guard < 16) {
    changed = false;
    guard += 1;
    for (let i = 0; i < parents.length; i += 1) {
      const parent = parents[i];
      const parentMid = getMarketId(parent);
      if (markets.includes(parentMid)) continue;

      const childMids = parent.consistsOf.map((ref) =>
        getMarketId(findByRef(ref))
      );
      const allChildrenSelected = childMids.every((mid) => markets.includes(mid));
      if (!allChildrenSelected) continue;

      childMids.forEach((mid) => {
        const idx = markets.indexOf(mid);
        if (idx >= 0) markets.splice(idx, 1);
      });
      if (!markets.includes(parentMid)) {
        markets.push(parentMid);
      }
      addIncludedFeedsToMarkets(markets, parent, findByRef, getMarketId);
      changed = true;
      break;
    }
  }
}

export function isExclusiveBlockedForFeed(
  feedRef,
  feedMarketId,
  selectedMarketIds,
  providers,
  getMarketId,
  graph
) {
  if (!feedRef || !graph.size) return false;
  if (!Array.isArray(selectedMarketIds)) return false;
  if (selectedMarketIds.includes(feedMarketId)) return false;

  for (let i = 0; i < selectedMarketIds.length; i += 1) {
    const p = findProviderByMarketId(selectedMarketIds[i], providers, getMarketId);
    if (p?.reference && refsMutuallyExclusive(feedRef, p.reference, graph)) {
      return true;
    }
  }
  return false;
}

export function collectBundleIncludedMarketIds(bundleFeeds, providers, getMarketId) {
  const includedFeedsSet = new Set();
  const findByRef = (ref) => providers.find((p) => p.reference === ref);

  const visitFeed = (feed, visitedRefs) => {
    if (!feed?.consistsOf || !Array.isArray(feed.consistsOf)) return;
    feed.consistsOf.forEach((ref) => {
      if (visitedRefs.has(ref)) return;
      visitedRefs.add(ref);
      const child = findByRef(ref);
      if (child) {
        includedFeedsSet.add(getMarketId(child));
        visitFeed(child, visitedRefs);
      }
    });
  };

  bundleFeeds.forEach((feed) => {
    visitFeed(feed, new Set());
  });
  return includedFeedsSet;
}

/** Cabinet selection key: feed display name (unique within platform-scoped list). */
export const cabinetMarketKey = (m) => m.name;
